import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAdminPage, type PageName } from "../navigation/pages";
import type { AppRoute } from "../navigation/routes";
import { fetchPmsCredentials, checkPmsHealth } from "../services/configApi";
import { fetchCurrentUser } from "../services/authApi";
import { fetchAnalysisHasCompleted } from "../services/analysisApi";
import { fetchOnboardingHasCompleted } from "../services/onboardingApi";
import { AccountDeactivatedError, AccountPendingApprovalError } from "../services/apiClient";
import { forgetPropertyCaches } from "../services/propertyCaches";
import { useSkin } from "../components/skin-provider";
import { useLanguage } from "../i18n/language-provider";
import type { ReadOnlyReason } from "../components/access/read-only-access";
import { isFrontDeskPage } from "../components/access/front-desk-pages";
import { availableAccountSections, isAccountSectionAvailable } from "../components/account/account-sections";
import { readUserFeatures, type UserFeature } from "../services/userFeatures";
import { readViewerKind, viewerKindOf } from "../services/viewerKind";
import type { PropertyAccessRole, UserRole, ViewerKind } from "../types/config";

const ONBOARDING_GATED_PAGES: PageName[] = ["dashboard", "recentBookings", "forecast", "strategies", "drafts"];

/**
 * Pages that do not need a property, and stay reachable while the PMS is
 * still unconfigured.
 *
 * The first three are the way *out* of that state. The account settings are
 * here for the opposite reason. Their payments are scoped to the tenant rather
 * than to a property, and the trial starts on the tenant the moment the
 * account exists — so the operator with nothing connected yet is exactly the
 * one deciding whether to buy, with a countdown running. Their security tokens
 * and AI-agent switch need no property either: a token reaches whatever
 * properties its holder runs, whenever they come. Refusing the page was also
 * silent, since the rail's account door is on every screen and simply did
 * nothing when clicked; a control that neither works nor says why is worse
 * than one that is not offered.
 */
const PMS_OPTIONAL_PAGES: PageName[] = ["settings", "connections", "onboarding", "account"];

/**
 * Pages a read-only viewer never opens. The strategy and the setup wizard are
 * the revenue manager's work, which a viewer checks the results of rather
 * than reads, and the study archive states it — every study names the
 * settings it recommends; and the connection settings hold the property's
 * credentials. The server refuses every call behind them anyway — this keeps
 * the shell from offering screens that would only fail.
 *
 * The account settings are not among them: their tokens and AI-agent switch
 * are the person's own, whatever property is open. Only their payments belong
 * to the account the open property sits in, which is not a viewer's — and
 * that is the section's to say (`opensAccountSection`), not the page's. A
 * viewer whom no administrator gave the AI agents has no section left, and is
 * refused the page by the same rule.
 */
const READ_ONLY_REFUSED_PAGES: PageName[] = ["strategies", "onboarding", "settings", "studyArchive"];

/**
 * The pages of those that staff — an administrator holding a read-only grant
 * on the open property — never open either. They review everything a member
 * reads, the strategy, the settings and the study archive included, and the
 * setup wizard only changes something: it writes the property's strategy at
 * every step. The payments, which pay for an account and never the one they
 * look at, are refused them by the account settings' own table.
 */
const STAFF_REFUSED_PAGES: PageName[] = ["onboarding"];

/**
 * The one screen behind those pages a viewer still opens: the form that adds a
 * property of their own. It holds no property's credentials — it connects a
 * new one, which joins the account the caller owns — and refused, somebody
 * whose every property is a read-only grant had no way to connect theirs.
 */
function addsOwnProperty(route: AppRoute): boolean {
    return route.page === "settings" && route.newConnection === true;
}

/**
 * Whether the account settings, or the section of them a route names, may be
 * opened, by the one rule the page itself follows (`account-sections.ts`). A
 * route naming no section is the page, which opens on one its reader may — so
 * it is refused only where there is none: a viewer, staff or a member given
 * some of the account's properties whom no administrator gave the AI agents,
 * the payments never being theirs. Such a
 * refusal falls back to the dashboard like any page's; a refused section
 * falls back to the page, which is then theirs (`App`), so neither loops.
 */
function opensAccountSection(
    target: PageName | AppRoute,
    readOnlyReason: ReadOnlyReason | null,
    features: readonly UserFeature[],
    wholeAccount: boolean | undefined,
): boolean {
    const access = { readOnlyReason, features, wholeAccount };
    if (availableAccountSections(access).length === 0) return false;
    if (typeof target === "string" || target.page !== "account" || !target.section) return true;
    return isAccountSectionAvailable(target.section, access);
}

/**
 * Whether a page may be shown: yes, no, or not known yet — an admin page
 * cannot be judged before `/api/me` has said who is asking.
 */
export type PageVerdict = boolean | "pending";

export type GoToRoute = (route: AppRoute, options?: { replace?: boolean }) => void;

/**
 * What the shell closes the open property's pages on (`isPageAllowed`,
 * `fallbackRoute`), with two answers read beside them: whether the property's
 * wizard was finished, which offers the wizard's way back to the dashboard
 * (`canExit`) and keeps the property's pages open while the wizard is re-run
 * (`handleRedoWizard`), and whether its analysis is missing, on which the rail
 * locks the pricing and analytics pages (`App`).
 */
interface SetupGates {
    pmsSetupRequired: boolean;
    pmsHealthError: string | null;
    onboardingRequired: boolean;
    analysisRequired: boolean;
    onboardingCompleted: boolean;
}

/** Nothing closed, and no wizard known to be finished. */
const NOTHING_CLOSED: SetupGates = {
    pmsSetupRequired: false,
    pmsHealthError: null,
    onboardingRequired: false,
    analysisRequired: false,
    onboardingCompleted: false,
};

/**
 * The gates while no PMS is configured: only the pages that need no property
 * are open, a refused page falls back to the wizard, and nothing is said of a
 * PMS fault, an analysis or a finished wizard. The first checks leave them for
 * a property whose credentials were never completed, and for somebody with no
 * property at all, whose credentials read says the same.
 */
const NO_PMS_CONFIGURED: SetupGates = { ...NOTHING_CLOSED, pmsSetupRequired: true, onboardingRequired: true };

/** The sentences a gate holds when the property's PMS check fails. */
interface PmsFaultWords {
    /** The PMS answered with a refusal that names no reason. */
    pmsUnhealthy: string;
    /** The check itself could not be made. */
    pmsVerifyFailed: string;
}

/**
 * The gates of the property open, read the one way both the first checks and
 * every change of the open property read them, so that a change leaves them
 * as a reload on the same property finds them. `wizardUnfinished` is the
 * property's own wizard saying it is not finished, once its PMS has answered:
 * a change to such a property moves into the wizard (`handleConnectionChanged`),
 * while the first checks move nobody.
 *
 * The PMS decides first. One that is not configured, or does not answer,
 * closes the property's pages whatever its wizard says, so the wizard is not
 * asked. Then the wizard: a finished one is authoritative, and the analysis is
 * optional after preset-based completion, so it is not asked either. Only
 * where the wizard cannot be read does the analysis decide. Not run, and the
 * wizard is required. Run, or not readable either, and nothing is closed,
 * since a failed read must not lock anybody out. A wizard that could not be
 * read is never counted as finished.
 *
 * The words are read once a fault is known, as the check always read them.
 */
async function readSetupGates(
    faultWords: () => PmsFaultWords,
): Promise<{ gates: SetupGates; wizardUnfinished: boolean }> {
    try {
        const credsRes = await fetchPmsCredentials();
        if (!credsRes.isConfigured) return { gates: NO_PMS_CONFIGURED, wizardUnfinished: false };

        const healthRes = await checkPmsHealth();
        if (!healthRes.healthy) {
            const pmsHealthError = healthRes.error || faultWords().pmsUnhealthy;
            return { gates: { ...NO_PMS_CONFIGURED, pmsHealthError }, wizardUnfinished: false };
        }
    } catch {
        return { gates: { ...NO_PMS_CONFIGURED, pmsHealthError: faultWords().pmsVerifyFailed }, wizardUnfinished: false };
    }

    try {
        const hasOnboarding = await fetchOnboardingHasCompleted();
        return hasOnboarding
            ? { gates: { ...NOTHING_CLOSED, onboardingCompleted: true }, wizardUnfinished: false }
            : { gates: { ...NOTHING_CLOSED, onboardingRequired: true }, wizardUnfinished: true };
    } catch {
        // The wizard could not be read: fall back to the analysis.
        try {
            const hasAnalysis = await fetchAnalysisHasCompleted();
            return {
                gates: hasAnalysis ? NOTHING_CLOSED : { ...NOTHING_CLOSED, onboardingRequired: true, analysisRequired: true },
                wizardUnfinished: false,
            };
        } catch {
            // Don't block
            return { gates: NOTHING_CLOSED, wizardUnfinished: false };
        }
    }
}

/**
 * Handles all one-time dashboard initialisation:
 * – user role/credits fetch
 * – PMS credential + health check
 * – onboarding / analysis completion gates
 *
 * The first checks say what is allowed rather than moving the user: the page
 * the address asks for is honoured when the gates allow it — a deep link to
 * billing is how GoPay returns an account still deciding whether to buy — and
 * the shell rewrites the address only when they refuse it. The moves the hook
 * does make are the ones a user's own action calls for: finishing or
 * re-running the wizard, and switching to a property whose PMS answers and
 * whose wizard is unfinished.
 */
/**
 * Whether the open property's access reaches its whole account, as
 * `/api/me` states it — and not told with no property open (nor with a
 * pointer the person no longer reaches, which names no role). There is then
 * no access to narrow the account: billing resolves to the account the
 * person owns (`resolveBillingTenant`), and its payments are theirs as they
 * were before scoped access. A "false" read there would refuse the account
 * page to exactly the owner still deciding whether to buy.
 */
function wholeAccountOfOpenProperty(user: {
    activeConnectionId: number | null;
    activeConnectionRole?: string | null;
    activeConnectionWholeAccount?: boolean;
}): boolean | undefined {
    if (user.activeConnectionId == null || user.activeConnectionRole == null) return undefined;
    return user.activeConnectionWholeAccount;
}

export function useDashboardInit(goTo: GoToRoute) {
    const { t } = useLanguage();
    const { refresh: refreshSkin } = useSkin();

    // User state
    const [userRole, setUserRole] = useState<UserRole>("user");
    const [userLoaded, setUserLoaded] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [userCredits, setUserCredits] = useState<number | null>(null);
    /**
     * What an administrator switched on for this person — the AI agents and
     * their security tokens. None until `/api/me` says otherwise, nothing
     * being on until an administrator turns it on.
     */
    const [features, setFeatures] = useState<UserFeature[]>([]);
    const [accountDeactivated, setAccountDeactivated] = useState(false);
    const [accountPendingApproval, setAccountPendingApproval] = useState(false);
    const [activeConnectionId, setActiveConnectionId] = useState<number | null>(null);
    /** Whether `/api/me` has named the open property — null before it has is "not known", not "none". */
    const [activeConnectionKnown, setActiveConnectionKnown] = useState(false);
    const [activeConnectionRole, setActiveConnectionRole] = useState<PropertyAccessRole | null>(null);
    /**
     * The kind of a viewer's grant on the open property: `management`, who
     * reads the prices and the analytics, or `front_desk`, the hotel's
     * receptionists, who read the prices and the reservations alone. Null for
     * every other role. Read through `viewerKindOf`, so a kind this page does
     * not know narrows and a server that names none reads as `management`.
     */
    const [activeConnectionViewerKind, setActiveConnectionViewerKind] = useState<ViewerKind | null>(null);
    /**
     * Whether the caller's access to the open property reaches its whole
     * account (`activeConnectionWholeAccount`): an owner, or a member whose
     * access covers every property. False for a member given some of the
     * account's properties, whom the account settings treat as they treat a
     * viewer — the account's payments are not theirs. Undefined where nothing
     * said, which reads as before (`account-sections.ts`).
     */
    const [accountWholeAccount, setAccountWholeAccount] = useState<boolean | undefined>(undefined);
    const [connectionVersion, setConnectionVersion] = useState(0);
    /**
     * Why the open property may only be read, for the screens that ask
     * (`ReadOnlyAccessProvider`): a viewer's grant — see
     * `READ_ONLY_REFUSED_PAGES` — or the same grant held by an administrator,
     * `staff`, who reads what a member reads. Null when it may be changed.
     */
    const readOnlyReason: ReadOnlyReason | null =
        activeConnectionRole === "viewer" || activeConnectionRole === "staff" ? activeConnectionRole : null;
    const isReadOnly = readOnlyReason !== null;
    /**
     * Whether the open property is the front desk's: a viewer whose grant is
     * of the kind `front_desk`. Every page off their list is refused them
     * (`components/access/front-desk-pages.ts`), on top of everything a
     * viewer is refused. Never staff, who has no kind.
     */
    const frontDesk = readOnlyReason === "viewer" && activeConnectionViewerKind === "front_desk";
    /**
     * The property the shell loads its pages for, once `/api/me` has named
     * it. The shell watches that it is still the one open — the open property
     * is shared by every tab, window and device — and the Prices page names it
     * on every change it makes. Undefined before `/api/me` has answered, after
     * it failed, and with no property open: then nothing is watched or named,
     * because a tab with none open shows nothing to mistake, and the change
     * that opens one (the wizard's PMS step) is made from it.
     */
    const loadedConnectionId = activeConnectionKnown && activeConnectionId !== null ? activeConnectionId : undefined;

    // PMS gate
    const [pmsCheckLoading, setPmsCheckLoading] = useState(true);
    const [pmsSetupRequired, setPmsSetupRequired] = useState(false);
    const [pmsHealthError, setPmsHealthError] = useState<string | null>(null);

    // Analysis gate
    const [analysisRequired, setAnalysisRequired] = useState(false);

    // Onboarding gate — replaces the old PMS + analysis overlays
    const [onboardingRequired, setOnboardingRequired] = useState(false);
    const [onboardingCompleted, setOnboardingCompleted] = useState(false);

    /** Every setup gate at once, from one reading of them (`readSetupGates`). */
    const applySetupGates = useCallback((gates: SetupGates) => {
        setPmsSetupRequired(gates.pmsSetupRequired);
        setPmsHealthError(gates.pmsHealthError);
        setOnboardingRequired(gates.onboardingRequired);
        setAnalysisRequired(gates.analysisRequired);
        setOnboardingCompleted(gates.onboardingCompleted);
    }, []);

    // Ref for translations so the PMS check effect doesn't re-run on lang switch
    const tRef = useRef(t);
    useEffect(() => { tRef.current = t; }, [t]);

    // Ref for navigation so callbacks do not depend on parent renders.
    const goToRef = useRef(goTo);
    useEffect(() => { goToRef.current = goTo; }, [goTo]);

    // Fetch user role on mount
    useEffect(() => {
        fetchCurrentUser()
            .then((user) => {
                setUserRole(user.role);
                setUserEmail(user.email);
                setUserId(user.id);
                setUserCredits(user.credits);
                setFeatures(readUserFeatures(user.features));
                setActiveConnectionId(user.activeConnectionId);
                setActiveConnectionKnown(true);
                setActiveConnectionRole(user.activeConnectionRole ?? null);
                setActiveConnectionViewerKind(viewerKindOf(user.activeConnectionRole, user.activeConnectionViewerKind));
                setAccountWholeAccount(wholeAccountOfOpenProperty(user));
            })
            .catch((err) => {
                if (err instanceof AccountPendingApprovalError) {
                    setAccountPendingApproval(true);
                    return;
                }
                if (err instanceof AccountDeactivatedError) {
                    setAccountDeactivated(true);
                    return;
                }
                console.warn("Failed to fetch user role:", err);
            })
            .finally(() => setUserLoaded(true));
    }, []);

    // Check PMS credentials + health + onboarding on mount (runs only once)
    useEffect(() => {
        let cancelled = false;

        async function checkPms() {
            setPmsCheckLoading(true);
            try {
                // `handleConnectionChanged` reads the gates the same way
                // whenever the open property changes while the tab is open.
                const { gates } = await readSetupGates(() => tRef.current.app);
                if (!cancelled) applySetupGates(gates);
            } finally {
                if (!cancelled) setPmsCheckLoading(false);
            }
        }

        checkPms();
        return () => { cancelled = true; };
    }, [applySetupGates]);

    // Re-check PMS after user saves credentials
    const handlePmsConfigured = useCallback(async () => {
        try {
            const healthRes = await checkPmsHealth();
            if (healthRes.healthy) {
                setPmsSetupRequired(false);
                setPmsHealthError(null);
            } else {
                setPmsHealthError(healthRes.error || tRef.current.app.connectionStillUnhealthy);
            }
        } catch {
            // keep setup required
        }
    }, []);

    /**
     * The gates, as one question the shell can ask of any page — whether it
     * came from a click or from the address bar. Administrators reach the
     * admin pages whatever state the property is in; nobody else reaches them
     * at all, and that cannot be decided before the role is known. The same
     * holds for the pages a read-only viewer is refused.
     *
     * A viewer is held by neither setup gate: finishing the wizard and fixing
     * the PMS connection are both the revenue manager's to do, so a gate that
     * waited for them would leave the viewer on a screen they cannot use. The
     * prices and analytics simply show what there is. Staff — an administrator
     * holding the same grant — are held by neither for the same reason, and
     * read every page a member reads but the two that only change something
     * (`STAFF_REFUSED_PAGES`).
     *
     * A page name answers for every screen but two: the settings page is both
     * the open property's connection, which a viewer is refused, and the form
     * that adds a property of their own, which they are not; and the account
     * settings hold the payments, which a viewer and staff are refused, beside
     * their own tokens, which they are not — so the shell asks with the whole
     * route. Which of those sections may be opened, and so which one the page
     * opens on, waits on who is asking like the pages a viewer is refused.
     *
     * The front desk — a viewer whose grant is of the kind `front_desk` — is
     * held to an allow-list on top of all that (`isFrontDeskPage`): the
     * overview, the Prices page, their properties, their own account and the
     * form that adds a property of their own. A page off that list therefore
     * waits on who is asking too, whoever asks: until `/api/me` has answered
     * nobody knows whether the reader is the front desk.
     */
    const isPageAllowed = useCallback(
        (target: PageName | AppRoute): PageVerdict => {
            const page = typeof target === "string" ? target : target.page;
            // Admin pages stay reachable before a PMS connection is configured.
            if (isAdminPage(page)) {
                if (!userLoaded) return "pending";
                return userRole === "admin";
            }
            if (!isFrontDeskPage(target)) {
                if (!userLoaded) return "pending";
                if (frontDesk) return false;
            }
            if (page === "account") {
                if (!userLoaded) return "pending";
                if (!opensAccountSection(target, readOnlyReason, features, accountWholeAccount)) return false;
            }
            if (READ_ONLY_REFUSED_PAGES.includes(page)) {
                if (!userLoaded) return "pending";
                if (isReadOnly) {
                    if (typeof target !== "string" && addsOwnProperty(target)) return true;
                    return readOnlyReason === "staff" && !STAFF_REFUSED_PAGES.includes(page);
                }
            }
            if (isReadOnly) return true;
            if (onboardingRequired && ONBOARDING_GATED_PAGES.includes(page)) return false;
            if (pmsSetupRequired && !PMS_OPTIONAL_PAGES.includes(page)) return false;
            return true;
        },
        [userLoaded, userRole, isReadOnly, readOnlyReason, frontDesk, features, accountWholeAccount, onboardingRequired, pmsSetupRequired],
    );

    /** Where a refused page sends the user instead. Nobody read-only has a wizard to go to. */
    const fallbackRoute = useMemo<AppRoute>(
        () => (!isReadOnly && (onboardingRequired || pmsSetupRequired) ? { page: "onboarding" } : { page: "dashboard" }),
        [isReadOnly, onboardingRequired, pmsSetupRequired],
    );

    // Page navigation gating
    const handleNavigate = useCallback(
        (route: AppRoute) => {
            if (isPageAllowed(route) !== true) return;
            goToRef.current(route);
        },
        [isPageAllowed],
    );

    /**
     * Read again what an administrator switched on for this person. Asked by
     * a section of the account settings the server has just refused for a
     * feature switched off while it was open, and by the users console when an
     * administrator switches one for themselves, so the navigator and the
     * rail's account door follow; nothing else about the person or the open
     * property is read again. A read that fails keeps what was known.
     */
    const refreshFeatures = useCallback(async () => {
        try {
            const user = await fetchCurrentUser();
            setFeatures(readUserFeatures(user.features));
        } catch {
            // Not knowing is no answer: keep what was known.
        }
    }, []);

    const handleCreditsChange = useCallback((newBalance: number) => {
        setUserCredits(newBalance);
    }, []);

    const handleAnalysisCompleted = useCallback(() => {
        setAnalysisRequired(false);
    }, []);

    const handleOnboardingCompleted = useCallback((destination: AppRoute = { page: "dashboard" }) => {
        setOnboardingRequired(false);
        setPmsSetupRequired(false);
        setAnalysisRequired(false);
        setOnboardingCompleted(true);
        goToRef.current(destination);
    }, []);

    const handleRedoWizard = useCallback(() => {
        // Users who already finished onboarding keep full navigation while revisiting it.
        if (!onboardingCompleted) setOnboardingRequired(true);
        goToRef.current({ page: "onboarding" });
    }, [onboardingCompleted]);

    // Re-fetch user info + gates when switching PMS connection
    const handleConnectionChanged = useCallback(async () => {
        // The room kinds' order and the price recalculation the Prices page
        // waits on are the property's just left, and the pages remount rather
        // than reload.
        forgetPropertyCaches();
        try {
            const user = await fetchCurrentUser();
            setUserRole(user.role);
            setUserEmail(user.email);
            setUserId(user.id);
            setUserCredits(user.credits);
            setFeatures(readUserFeatures(user.features));
            setActiveConnectionId(user.activeConnectionId);
            setActiveConnectionKnown(true);
            setActiveConnectionRole(user.activeConnectionRole ?? null);
            setActiveConnectionViewerKind(viewerKindOf(user.activeConnectionRole, user.activeConnectionViewerKind));
            setAccountWholeAccount(wholeAccountOfOpenProperty(user));
            // Nobody read-only can run the wizard: a viewer, or staff.
            const switchedToReadOnly = user.activeConnectionRole === "viewer" || user.activeConnectionRole === "staff";

            // The gates are read by the reading the first checks make, so a
            // change leaves them as a reload on the property now open finds
            // them, whichever property the tab was loaded on. Read apart, they
            // kept answers of the property left: its finished wizard offered
            // the wizard's way back to a dashboard the gates refused, its
            // unfinished one closed a set-up property's pages while that
            // property's wizard was re-run, and an analysis a reload does not
            // ask once the wizard is finished locked the rail's pricing and
            // analytics.
            //
            // With no property open any more (the last one was deleted, or
            // the last access to one ended, while this tab was open) there is
            // none to read, and the gates are the ones the first checks leave
            // somebody with no property: only the setup pages are open, a
            // refused page falls back to the wizard, and nothing is kept of
            // the PMS fault, the analysis or the finished wizard of a property
            // that is gone. The shell used to go on offering its overview,
            // prices and strategy until a reload.
            const { gates, wizardUnfinished } = user.activeConnectionId
                ? await readSetupGates(() => tRef.current.app)
                : { gates: NO_PMS_CONFIGURED, wizardUnfinished: false };
            applySetupGates(gates);

            // A property whose PMS answers and whose wizard is unfinished
            // opens in the wizard, in place: the entry being left belongs to
            // the property just switched away from, and Back must not lead to
            // one of its pages. Nobody read-only can run the wizard, so they
            // stay where they are. So does everybody where the PMS does not
            // answer, the wizard cannot be read or the last property is gone,
            // as on a reload: the pages that need a property are closed, the
            // shell rewrites the address of one the gates now refuse, and the
            // Connections page, a connection's settings and the account stay
            // open to whoever is on them.
            if (wizardUnfinished && !switchedToReadOnly) {
                goToRef.current({ page: "onboarding" }, { replace: true });
            }

            // Load the Appearance skin for the newly active connection (its own
            // uiTheme preference, or the PMS it connects to when set to "auto").
            refreshSkin();

            // Increment version so PmsConnectionSwitcher re-fetches the connection list
            setConnectionVersion((v) => v + 1);
        } catch {
            // ignore
        }
    }, [refreshSkin, applySetupGates]);

    /**
     * A property has been opened by a screen that knows the caller's access to
     * it — the Connections page's list names it on every card, and the admin
     * console opens one an administrator has added themselves to read-only, as
     * `staff`. Said before `/api/me` answers, because that screen moves on at
     * once — to the property's settings, into its setup wizard, or onto its
     * prices — and the next page is judged by the property open: by the
     * read-only one open a moment ago, the move into the caller's own would be
     * turned away.
     *
     * For a viewer the screen names the kind of the grant as well. One that
     * names none is held to the front desk, the narrower kind, until `/api/me`
     * says which it is: by the kind of the property open a moment ago, a
     * receptionist would be shown the analytics for as long as that takes.
     */
    const handleConnectionOpened = useCallback(
        (role: PropertyAccessRole, viewerKind?: ViewerKind | null) => {
            setActiveConnectionRole(role);
            setActiveConnectionViewerKind(
                role === "viewer" ? (viewerKind == null ? "front_desk" : readViewerKind(viewerKind)) : null,
            );
            // An owner's account is theirs; whether a member's is, `/api/me`
            // says once it answers — until then, as a server that says nothing.
            setAccountWholeAccount(role === "owner" ? true : undefined);
            return handleConnectionChanged();
        },
        [handleConnectionChanged],
    );

    /**
     * The new-connection form has created a property, which the server made
     * the caller an owner of — it joins the account they own — and opened for
     * them. Said before `/api/me` answers: the form moves on to the new
     * property's settings at once, and a viewer who added a property of their
     * own would otherwise still be judged as one there, and turned away.
     */
    const handleConnectionCreated = useCallback(() => handleConnectionOpened("owner"), [handleConnectionOpened]);

    return {
        userRole,
        userLoaded,
        userEmail,
        userId,
        userCredits,
        features,
        accountDeactivated,
        accountPendingApproval,
        activeConnectionId,
        loadedConnectionId,
        activeConnectionRole,
        activeConnectionViewerKind,
        accountWholeAccount,
        isReadOnly,
        readOnlyReason,
        frontDesk,
        connectionVersion,
        pmsCheckLoading,
        pmsSetupRequired,
        pmsHealthError,
        analysisRequired,
        onboardingRequired,
        onboardingCompleted,
        isPageAllowed,
        fallbackRoute,
        handlePmsConfigured,
        handleNavigate,
        handleCreditsChange,
        refreshFeatures,
        handleAnalysisCompleted,
        handleOnboardingCompleted,
        handleRedoWizard,
        handleConnectionChanged,
        handleConnectionCreated,
        handleConnectionOpened,
    };
}
